/**
 * Hook: useAuthSession
 * 
 * Manages the session lifecycle and authentication state
 * Handles session initialization, auth listener setup, and session errors
 * 
 * @returns {Object} { session, setSession, sessionLoading, authError, isSignedIn }
 */

import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

export const useAuthSession = () => {
  const [session, setSession] = useState(null)
  const [sessionLoading, setSessionLoading] = useState(true)
  const [authError, setAuthError] = useState('')
  const [lastAuthEvent, setLastAuthEvent] = useState('')
  const [isRecoverySession, setIsRecoverySession] = useState(false)

  useEffect(() => {
    let isMounted = true

    const loadSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession()
        if (!isMounted) return

        if (error) {
          setAuthError(error.message || 'Unable to verify your session.')
        } else {
          setLastAuthEvent('INITIAL_SESSION')
          if (!data?.session) {
            setIsRecoverySession(false)
          }
          setSession(data?.session || null)
        }
      } catch (error) {
        if (isMounted) {
          setAuthError(error?.message || 'Session verification failed.')
        }
      } finally {
        if (isMounted) {
          setSessionLoading(false)
        }
      }
    }

    loadSession()

    const authListener = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (isMounted) {
        setLastAuthEvent(String(event || ''))
        if (event === 'PASSWORD_RECOVERY') {
          setIsRecoverySession(true)
        } else if (event === 'SIGNED_OUT') {
          setIsRecoverySession(false)
        }
        setSession(nextSession)
        setSessionLoading(false)
      }
    })

    return () => {
      isMounted = false
      if (authListener?.data?.subscription) {
        authListener.data.subscription.unsubscribe()
      }
    }
  }, [])

  return {
    session,
    setSession,
    sessionLoading,
    authError,
    lastAuthEvent,
    isRecoverySession,
    isSignedIn: Boolean(session)
  }
}
